import React, { useState, useRef, useEffect } from 'react';
import { Trophy, Coins, Gift, Info, Zap, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useWheelSpin } from '../hooks/useWheelSpin';

interface WheelSegment {
  id: number;
  label: string;
  value: string;
  color: string;
  probability: number;
}

interface FloatingObject {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  type: 'coin' | 'bubble';
}

const SpinTheWheel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { eligibleDeposit, loading: depositLoading, error: depositError, spinWheel } = useWheelSpin(user?.id);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState<WheelSegment | null>(null);
  const [floatingObjects, setFloatingObjects] = useState<FloatingObject[]>([]);
  const [spinError, setSpinError] = useState<string | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  const segments: WheelSegment[] = [
    { id: 1, label: '5%', value: '5', color: '#0891b2', probability: 40 },
    { id: 2, label: '10%', value: '10', color: '#2563eb', probability: 20 },
    { id: 3, label: '20%', value: '20', color: '#14b8a6', probability: 8 },
    { id: 4, label: '❌', value: '0', color: '#ef4444', probability: 31.34 },
    { id: 5, label: '50%', value: '50', color: '#0891b2', probability: 0.5 },
    { id: 6, label: '70%', value: '70', color: '#2563eb', probability: 0.1 },
    { id: 7, label: '80%', value: '80', color: '#14b8a6', probability: 0.05 },
    { id: 8, label: '100%', value: '100', color: '#10b981', probability: 0.01 },
  ];

  const totalSegments = segments.length;
  const segmentAngle = 360 / totalSegments;

  useEffect(() => {
    const objects: FloatingObject[] = [];
    for (let i = 0; i < 15; i++) {
      objects.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 30 + 20,
        delay: Math.random() * 5,
        duration: Math.random() * 3 + 4,
        type: Math.random() > 0.5 ? 'coin' : 'bubble',
      });
    }
    setFloatingObjects(objects);
  }, []);

  const handleSpinWheel = async () => {
    if (isSpinning || !eligibleDeposit) return;

    setIsSpinning(true);
    setResult(null);
    setSpinError(null);

    const fullRotations = 5 + Math.floor(Math.random() * 5);
    const rand = Math.random() * 100;
    let cumulativeProbability = 0;
    let winningSegment = segments[0];

    for (const segment of segments) {
      cumulativeProbability += segment.probability;
      if (rand <= cumulativeProbability) {
        winningSegment = segment;
        break;
      }
    }

    const segmentIndex = segments.findIndex(s => s.id === winningSegment.id);
    const targetAngle = segmentIndex * segmentAngle;
    const pointerOffset = 90;
    const centerOffset = segmentAngle / 2;
    const finalRotation = rotation + (fullRotations * 360) + (360 - targetAngle - pointerOffset + centerOffset);

    setRotation(finalRotation);

    setTimeout(async () => {
      setIsSpinning(false);
      setResult(winningSegment);

      const winningValue = parseInt(winningSegment.value);

      if (winningValue > 0) {
        try {
          await spinWheel(winningValue);
        } catch (error) {
          console.error('Error processing spin:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to credit winnings. Please contact support.';
          setSpinError(errorMessage);
        }
      } else {
        try {
          await spinWheel(0);
        } catch (error) {
          console.error('Error processing spin:', error);
        }
      }
    }, 4000);
  };

  return (
    <div className="min-h-screen p-4 md:p-8 bg-slate-50/95">
      <div className="max-w-7xl mx-auto px-4">
        <div className="border-b pb-6 mb-8" style={{ borderColor: 'rgba(0, 0, 0, 0.08)' }}>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ color: '#1e293b' }}>
            {t('spinWheel.title')}
          </h1>
          <p className="text-sm uppercase tracking-wider mt-2" style={{ color: '#64748b' }}>
            {t('spinWheel.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-1 space-y-6">
            <div className="relative overflow-hidden group" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)'
            }}>
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10" style={{
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.8) 0%, transparent 70%)',
                filter: 'blur(20px)'
              }}></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
                  <div className="flex items-center justify-center" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.08))',
                    border: '2px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <Info size={20} style={{ color: '#3b82f6' }} />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('spinWheel.howToPlay')}</h3>
                </div>
                <p className="leading-relaxed text-sm" style={{ color: '#475569' }}>
                  {t('spinWheel.howToPlayDesc')}
                </p>
              </div>
            </div>

            <div className="relative overflow-hidden group" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
              border: '1px solid rgba(251, 191, 36, 0.2)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.1)'
            }}>
              <div className="absolute bottom-0 left-0 w-32 h-32 opacity-10" style={{
                background: 'radial-gradient(circle, rgba(251, 191, 36, 0.8) 0%, transparent 70%)',
                filter: 'blur(20px)'
              }}></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(251, 191, 36, 0.15)' }}>
                  <div className="flex items-center justify-center" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1))',
                    border: '2px solid rgba(251, 191, 36, 0.3)'
                  }}>
                    <Coins size={20} style={{ color: '#f59e0b' }} />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('spinWheel.lastDeposit')}</h3>
                </div>
                {depositLoading ? (
                  <div className="text-sm" style={{ color: '#475569' }}>{t('common.loading')}</div>
                ) : eligibleDeposit ? (
                  <>
                    <div className="text-2xl font-mono font-bold mb-2" style={{ color: '#1e293b' }}>
                      ${Number(eligibleDeposit.amount).toLocaleString()}
                    </div>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: '#78716c' }}>
                      {t('spinWheel.potentialWin')}
                    </div>
                    <div className="text-sm font-mono font-bold" style={{ color: '#f59e0b' }}>
                      ${(Number(eligibleDeposit.amount) * 0.05).toFixed(2)} - ${Number(eligibleDeposit.amount).toLocaleString()}
                    </div>
                  </>
                ) : (
                  <div className="text-sm" style={{ color: '#475569' }}>
                    {t('spinWheel.noEligibleDeposit') || 'No eligible deposits. Make a deposit to spin!'}
                  </div>
                )}
              </div>
            </div>

            <div className="relative overflow-hidden group" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fee2e2 100%)',
              border: '1px solid rgba(217, 18, 34, 0.2)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(217, 18, 34, 0.1)'
            }}>
              <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10" style={{
                background: 'radial-gradient(circle, rgba(217, 18, 34, 0.8) 0%, transparent 70%)',
                filter: 'blur(20px)'
              }}></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(217, 18, 34, 0.15)' }}>
                  <div className="flex items-center justify-center" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(217, 18, 34, 0.08))',
                    border: '2px solid rgba(217, 18, 34, 0.25)'
                  }}>
                    <Zap size={20} style={{ color: '#d91222' }} />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('spinWheel.rulesTitle')}</h3>
                </div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.2), rgba(217, 18, 34, 0.1))',
                      border: '1px solid rgba(217, 18, 34, 0.3)'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#d91222'
                      }}></div>
                    </div>
                    <span style={{ color: '#475569' }}>{t('spinWheel.rule1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.2), rgba(217, 18, 34, 0.1))',
                      border: '1px solid rgba(217, 18, 34, 0.3)'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#d91222'
                      }}></div>
                    </div>
                    <span style={{ color: '#475569' }}>{t('spinWheel.rule2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.2), rgba(217, 18, 34, 0.1))',
                      border: '1px solid rgba(217, 18, 34, 0.3)'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#d91222'
                      }}></div>
                    </div>
                    <span style={{ color: '#475569' }}>{t('spinWheel.rule3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.2), rgba(217, 18, 34, 0.1))',
                      border: '1px solid rgba(217, 18, 34, 0.3)'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#d91222'
                      }}></div>
                    </div>
                    <span style={{ color: '#475569' }}>{t('spinWheel.rule4')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 flex flex-col items-center justify-center gap-12">
          <div className="relative w-full max-w-[700px] aspect-square flex items-center justify-center">
            <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-3xl animate-pulse"></div>

            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 z-20">
              <div className="relative">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <path
                    d="M30 45 L15 15 L30 20 L45 15 Z"
                    fill="url(#arrowGradient)"
                    stroke="rgba(255, 255, 255, 0.8)"
                    strokeWidth="2"
                    filter="drop-shadow(0 4px 12px rgba(34, 211, 238, 0.8))"
                  />
                  <defs>
                    <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="50%" stopColor="#f59e0b" />
                      <stop offset="100%" stopColor="#ea580c" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 animate-pulse" style={{
                  background: 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%)',
                  filter: 'blur(10px)',
                }}></div>
              </div>
            </div>

            <div className="relative w-[95%] h-[95%]">
              <div className="absolute inset-0 rounded-full" style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(34, 211, 238, 0.8), rgba(59, 130, 246, 0.6), rgba(147, 51, 234, 0.4))',
                boxShadow: '0 0 80px rgba(34, 211, 238, 0.6), 0 0 120px rgba(59, 130, 246, 0.4), inset 0 0 60px rgba(255, 255, 255, 0.1)',
              }}>
                <div className="absolute inset-2 rounded-full bg-slate-900/50 backdrop-blur-sm border-4 border-cyan-500/50" style={{
                  boxShadow: 'inset 0 0 40px rgba(34, 211, 238, 0.3)',
                }}>

                  <div
                    ref={wheelRef}
                    className="absolute inset-8 rounded-full overflow-hidden"
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                      boxShadow: 'inset 0 4px 20px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    <svg className="w-full h-full" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                      {segments.map((segment, index) => {
                        const startAngle = (index * 360) / totalSegments;
                        const endAngle = ((index + 1) * 360) / totalSegments;
                        const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

                        const startX = 50 + 50 * Math.cos((startAngle * Math.PI) / 180);
                        const startY = 50 + 50 * Math.sin((startAngle * Math.PI) / 180);
                        const endX = 50 + 50 * Math.cos((endAngle * Math.PI) / 180);
                        const endY = 50 + 50 * Math.sin((endAngle * Math.PI) / 180);

                        const midAngle = startAngle + (endAngle - startAngle) / 2;
                        const textRadius = 32;
                        const textX = 50 + textRadius * Math.cos((midAngle * Math.PI) / 180);
                        const textY = 50 + textRadius * Math.sin((midAngle * Math.PI) / 180);

                        return (
                          <g key={segment.id}>
                            <path
                              d={`M 50 50 L ${startX} ${startY} A 50 50 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                              fill={segment.color}
                              stroke="rgba(255, 255, 255, 0.15)"
                              strokeWidth="0.3"
                            />
                            <text
                              x={textX}
                              y={textY}
                              fill="white"
                              fontSize="7"
                              fontWeight="900"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              transform={`rotate(${midAngle + 90} ${textX} ${textY})`}
                              style={{
                                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
                                paintOrder: 'stroke fill',
                                stroke: 'rgba(0, 0, 0, 0.3)',
                                strokeWidth: '0.5px',
                              }}
                            >
                              {segment.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>

                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full" style={{
                      background: 'radial-gradient(circle at 30% 30%, #22d3ee, #3b82f6, #8b5cf6)',
                      boxShadow: '0 0 30px rgba(34, 211, 238, 0.8), inset 0 0 20px rgba(255, 255, 255, 0.3)',
                    }}>
                      <div className="absolute inset-2 rounded-full bg-gradient-to-br from-cyan-300/50 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="text-center p-6 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #dcfce7 100%)',
              border: '2px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(34, 197, 94, 0.15)'
            }}>
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10" style={{
                background: 'radial-gradient(circle, rgba(34, 197, 94, 0.8) 0%, transparent 70%)',
                filter: 'blur(15px)'
              }}></div>
              <div className="relative">
                <div className="text-xs uppercase tracking-wider mb-2 font-bold" style={{ color: '#78716c' }}>Result</div>
                <div className="text-2xl font-mono font-bold" style={{ color: '#1e293b' }}>
                  {result.value === '0' ? 'No Credit Applied' : `${result.label} Deposit Credit Applied`}
                </div>
              </div>
            </div>
          )}

          {!result && (
            <div className="text-xl font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>
              Execute Promotional Spin
            </div>
          )}

          {spinError && (
            <div className="p-4 flex items-start gap-3 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fee2e2 100%)',
              border: '2px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.1)'
            }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
              <p className="text-sm" style={{ color: '#7f1d1d' }}>{spinError}</p>
            </div>
          )}

          {!eligibleDeposit && !depositLoading && (
            <div className="p-4 flex items-start gap-3 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
              border: '2px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.1)'
            }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
              <p className="text-sm" style={{ color: '#78350f' }}>
                {t('spinWheel.noEligibleDeposit') || 'Make a deposit to spin the wheel!'}
              </p>
            </div>
          )}

          <button
            onClick={handleSpinWheel}
            disabled={isSpinning || !eligibleDeposit || depositLoading}
            className="bg-[#d91222] hover:bg-[#c01020] disabled:bg-slate-800 text-white px-12 py-4 font-medium transition-all uppercase tracking-wider text-sm border border-[#d91222] hover:border-[#c01020] disabled:border-slate-800 disabled:cursor-not-allowed rounded-xl"
          >
            <span className="text-white">{isSpinning ? `${t('common.loading')}` : t('spinWheel.spinButton')}</span>
          </button>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #dcfce7 100%)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)'
          }}>
            <div className="absolute top-0 left-0 w-32 h-32 opacity-10" style={{
              background: 'radial-gradient(circle, rgba(34, 197, 94, 0.8) 0%, transparent 70%)',
              filter: 'blur(20px)'
            }}></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(34, 197, 94, 0.15)' }}>
                <div className="flex items-center justify-center" style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))',
                  border: '2px solid rgba(34, 197, 94, 0.3)'
                }}>
                  <Trophy size={20} style={{ color: '#22c55e' }} />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('spinWheel.prizes')}</h3>
              </div>
              <p className="mb-4 text-sm" style={{ color: '#475569' }}>
                {t('spinWheel.prizesDesc')}
              </p>
              <div className="space-y-2">
                {[
                  { percent: '100%', gradient: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(217, 18, 34, 0.08))' },
                  { percent: '80%', gradient: 'linear-gradient(135deg, rgba(251, 146, 60, 0.15), rgba(251, 146, 60, 0.08))' },
                  { percent: '70%', gradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(251, 191, 36, 0.08))' },
                  { percent: '50%', gradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.08))' },
                  { percent: '20%', gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.08))' },
                  { percent: '10%', gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(99, 102, 241, 0.08))' },
                  { percent: '5%', gradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(168, 85, 247, 0.08))' },
                ].map((prize) => (
                  <div key={prize.percent} className="flex items-center justify-between p-3 transition-all" style={{
                    background: prize.gradient,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '10px'
                  }}>
                    <span className="font-mono text-sm font-bold" style={{ color: '#1e293b' }}>{prize.percent}</span>
                    <span className="font-mono text-sm font-bold" style={{ color: '#64748b' }}>
                      {eligibleDeposit ? `$${((Number(eligibleDeposit.amount) * parseInt(prize.percent)) / 100).toFixed(2)}` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SpinTheWheel;
